import Add16 from '@carbon/icons-react/es/add/16';
import Close16 from '@carbon/icons-react/es/close/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Moment } from 'moment';
import Mousetrap from 'mousetrap';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { TimesheetEntryType } from 'api/timesheets';
import { Category } from 'api/timesheets/categories';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Loading } from 'components/atoms/loading';
import { Select } from 'components/atoms/select';
import { Option } from 'components/atoms/select/types';
import { DeleteButton } from 'components/organisms/timesheets/common/style';
import {
  isTimeEntryInCreating,
  isTimeEntryNotLoaded,
  isTimeEntryPartiallyFilled,
} from 'components/organisms/timesheets/common/utils';
import { OutlineError, SingleLineEllipsis } from 'components/styles';
import { getRandomColor } from 'utils/get-random-color';
import { TimesheetTableTimeEntry, TimesheetWeeklyRowTask } from '../../types';
import { NotesInput } from './notes-input';
import { TimeDurationInput } from './time-duration-input';

const Wrapper = styled.div`
  width: 660px;
`;

const TitleWrapper = styled.div`
  width: 90%;
`;

const Mandatory = styled.span`
  color: var(--red-50);
`;

const Header = styled.div`
  background: var(--coolest-gray-10);
  padding: var(--spacing-05);
  display: flex;
  justify-content: space-between;
`;
const Title = styled.div`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  ${SingleLineEllipsis};
`;
const SecondaryTitle = styled.div`
  font-size: 12px;
  opacity: 0.5;
  color: #000000;
`;

const ErrorWrapper = styled.div`
  .error {
    opacity: 0;
    color: var(--error-color);
    height: var(--layout-01);
  }
  .errorWrapper {
    outline-color: var(--error-color) !important;
  }
  .show {
    opacity: 1;
  }
`;

const CheckboxWrapper = styled.div`
  padding: var(--spacing-03);
`;

const StyledTable = styled(Table)`
  padding: 0px 8px;
  /* width: 660px; */
  overflow-y: auto;
  max-height: 240px;

  .ant-table-thead > tr > th {
    background: transparent;
    font-size: 12px;
    color: var(--gray-80);
    padding: 8px 8px 0;
    border: none;
  }
  .ant-table-body {
    overflow: auto !important;
    overflow-x: hidden !important;
  }
  .ant-table-tbody > tr > td {
    padding: 0;
    vertical-align: top;
  }

  .ant-table-placeholder {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 8px;
    border: none;
  }
  .ant-table-placeholder {
    display: none;
  }
`;

const TimeInput = styled(TimeDurationInput)<{ hasError }>`
  ${ifProp(
    'hasError',
    css`
      ${OutlineError};
      &:focus {
        box-shadow: none;
        ${OutlineError};
      }
    `,
  )};
`;

const ErrorMessageWrapper = styled.div`
  font-size: var(--font-size-sm);
`;

const ActionRow = styled.div`
  padding: var(--spacing-03);
`;

const DurationInput = (props) => {
  const [hasError, setError] = useState(false);

  return (
    <TimeInput hasError={hasError} onErrorStateChange={setError} {...props} />
  );
};

interface Props {
  task: TimesheetWeeklyRowTask;
  entries: TimesheetTableTimeEntry[];
  day: Moment;
  editable: boolean;
  timeEntryCategories: Category[];
  updateTimeEntry: (timeEntry: TimesheetTableTimeEntry, value) => void;
  addTimeEntry: () => void;
  deleteTimeEntry: (timeEntryId: number) => void;
  onClose: (event) => void;
  parentVisible: boolean;
  mandateCategory: boolean;
}

const isNonProjectAdhocTask = (task: TimesheetWeeklyRowTask) =>
  task.entryType === TimesheetEntryType.AdhocEntry && task.project == null;

const Loader = styled(Loading)``;
const InputWrapper = styled.div`
  position: relative;
  ${Loader} {
    position: absolute;
    right: var(--spacing-02);
    top: 10px;
  }
`;
const getColumnConfig = ({
  task,
  categoryOptions,
  updateTimeEntry,
  deleteTimeEntry,
  editable,
  parentVisible,
  numberOfEntries,
  wrapperRef,
  mandateCategory,
}: {
  task: TimesheetWeeklyRowTask;
  categoryOptions: Option[];
  editable: boolean;
  updateTimeEntry: (timeEntry: TimesheetTableTimeEntry, values) => void;
  deleteTimeEntry: (timeEntryId: number) => void;
  parentVisible: boolean;
  numberOfEntries: number;
  wrapperRef;
  mandateCategory: boolean;
}) => {
  return [
    {
      title: (
        <>
          <FormattedMessage id="timesheets.column.time_tracked" />
          {mandateCategory ? <Mandatory>*</Mandatory> : null}
        </>
      ),
      render: (_, record: TimesheetTableTimeEntry, index) => {
        const isEntryCreating = isTimeEntryInCreating(record);
        const isEntryPartiallyFilled = isTimeEntryPartiallyFilled(record);

        return (
          <InputWrapper>
            <DurationInput
              minutes={record.minutes}
              disabled={!editable || isEntryCreating}
              autoFocus={
                isEntryPartiallyFilled
                  ? false
                  : editable && parentVisible && index === numberOfEntries - 1
              }
              onMinutesChange={(value) => {
                if (value !== record.minutes) {
                  updateTimeEntry(record, {
                    minutes: value ?? 0,
                  });
                }
              }}
            />
            {isEntryCreating ? <Loader /> : null}
          </InputWrapper>
        );
      },
      width: '120px',
    },
    {
      title: <FormattedMessage id="timesheets.column.billable" />,
      width: '70px',
      render: (_, record: TimesheetTableTimeEntry) => {
        const isFieldDisabled =
          isNonProjectAdhocTask(task) ||
          !editable ||
          isTimeEntryNotLoaded(record);

        return (
          <CheckboxWrapper>
            <Checkbox
              checked={record.billable}
              disabled={isFieldDisabled}
              onChange={() => {
                updateTimeEntry(record, {
                  billable: !record.billable,
                });
              }}
            />
          </CheckboxWrapper>
        );
      },
    },
    {
      title: (
        <>
          <FormattedMessage id="timesheets.column.choose_category" />
          {mandateCategory ? <Mandatory>*</Mandatory> : null}
        </>
      ),
      render: (_, record: TimesheetTableTimeEntry) => {
        const isEntryPartiallyFilled = isTimeEntryPartiallyFilled(record);
        const selectedValue =
          categoryOptions.find(
            (category) => record.category?.categoryId === category.value,
          ) ?? null;

        return (
          <ErrorWrapper>
            <Select
              options={categoryOptions}
              value={selectedValue}
              autoFocus={isEntryPartiallyFilled}
              onBlur={(e) => {
                if (isEntryPartiallyFilled) {
                  // basically I want to toggle select wrapper border which is kind difficult because this isn't a component and is also a poperover on top of another popover.

                  const el =
                    e.target.parentElement.parentElement.parentElement
                      .parentElement;

                  setTimeout(() => {
                    el.classList.add('errorWrapper');
                    document.querySelector('#error-msg')?.classList.add('show');
                  }, 10);
                }
              }}
              onFocus={(e) => {
                e.target.parentElement.parentElement.parentElement.parentElement.classList.remove(
                  'errorWrapper',
                );
                document.querySelector('#error-msg')?.classList.remove('show');
              }}
              type="tag"
              menuPortalTarget={wrapperRef?.current}
              menuPosition="fixed"
              isDisabled={!editable || !record.minutes}
              onChange={(selected) => {
                updateTimeEntry(record, {
                  category: selected
                    ? {
                        categoryName: selected.label,
                        categoryId: selected.value,
                      }
                    : null,
                });
              }}
            />
            {isEntryPartiallyFilled ? (
              <ErrorMessageWrapper id="error-msg" className="error">
                <FormattedMessage id="input.required" />
              </ErrorMessageWrapper>
            ) : null}
          </ErrorWrapper>
        );
      },
      width: '200px',
    },
    {
      title: <FormattedMessage id="timesheets.column.notes" />,
      render: (_, record: TimesheetTableTimeEntry) => {
        return (
          <NotesInput
            value={record.notes}
            disabled={!editable || isTimeEntryNotLoaded(record)}
            onUpdate={(notes) => {
              updateTimeEntry(record, {
                notes,
              });
            }}
          />
        );
      },
      width: '190px',
    },
    {
      title: '',
      width: '10px',
      render: (_, record: TimesheetTableTimeEntry) => {
        return (
          <DeleteButton
            disabled={!editable || isTimeEntryInCreating(record)}
            icon={<TrashCan16 />}
            onClick={() => {
              deleteTimeEntry(record.timeEntryId);
            }}
          />
        );
      },
    },
  ];
};

const getTitle = (task: TimesheetWeeklyRowTask) => {
  switch (task.entryType) {
    case TimesheetEntryType.TaskEntry:
      return task.name;
    case TimesheetEntryType.ProjectEntry:
      return task.project.name;
    case TimesheetEntryType.PhaseEntry:
      return task.projectPhase.name;
    default:
      return task.activityName;
  }
};

const isAddDisabled = (entries: TimesheetTableTimeEntry[]) => {
  if (!entries.length) {
    return true;
  }
  const lastEntry = entries[entries.length - 1];

  if (lastEntry.minutes === 0) {
    return true;
  }
  if (
    isTimeEntryInCreating(lastEntry) ||
    isTimeEntryPartiallyFilled(lastEntry)
  ) {
    return true;
  }

  return false;
};

export const AddTimeEntry: FC<Props> = ({
  task,
  entries,
  day,
  editable,
  timeEntryCategories,
  addTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  onClose,
  parentVisible,
  mandateCategory,
}) => {
  const categoryOptions = useMemo(
    () =>
      timeEntryCategories
        .map((category) => ({
          label: category.name,
          value: category.id,
          color: getRandomColor(category.id),
        }))
        .sort((a, b) => naturalCompare(a.label, b.label)),
    [timeEntryCategories],
  );
  const ref = useRef(null);
  const columns: ColumnsType<any> = getColumnConfig({
    task,
    editable,
    categoryOptions,
    updateTimeEntry,
    deleteTimeEntry,
    parentVisible,
    numberOfEntries: entries.length,
    wrapperRef: ref,
    mandateCategory,
  });

  useEffect(() => {
    const mousetrap = new Mousetrap();

    mousetrap.stopCallback = () => {
      return false;
    };

    mousetrap.bind('escape', (e) => {
      return onClose(e);
    });

    return () => {
      mousetrap.unbind('escape');
    };
  });

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <Header>
        <TitleWrapper>
          <Title>{getTitle(task)}</Title>
          <SecondaryTitle>{day.format('ddd, DD MMM')}</SecondaryTitle>
        </TitleWrapper>
        <Button type="text" size="small" icon={<Close16 />} onClick={onClose} />
      </Header>
      <div ref={ref}>
        <StyledTable
          columns={columns}
          dataSource={entries}
          pagination={false}
          rowKey="timeEntryId"
        />
        <ActionRow>
          {editable ? (
            <Button
              type="link"
              disabled={isAddDisabled(entries)}
              icon={<Add16 />}
              onClick={addTimeEntry}
            >
              <FormattedMessage id="timesheets.buttons.add_another_time_entry" />
            </Button>
          ) : null}
        </ActionRow>
      </div>
    </Wrapper>
  );
};
